import { useEffect, useState } from "react";

import useAppStore from "../store/appStateZustand";
import Select from "./Select";

/**
 * Clone Environment Selector
 *
 * A dropdown component for selecting which clone environment to use.
 * Only visible when VITE_STAGE is "dev" or "local".
 *
 * PLAT-2686 follow-up (part 2): the clone list used to be hardcoded to
 * 1-11. On-demand clones (12+) can now be created/destroyed via the
 * clone-lifecycle Jenkins pipeline, so instead this fetches the current
 * list from a small Lambda (publishing-dashboard/infra/lambda/clone-list)
 * that lists which clone-<N>-sa-api Pulumi stacks actually currently
 * exist. New clones appear here and destroyed ones drop out, without a
 * frontend redeploy. If VITE_CLONE_LIST_URL isn't configured, or the
 * fetch fails, this falls back to the static 1-11 list so the selector
 * stays usable either way.
 *
 * When a clone is selected, it updates both the cloneEnvironment state
 * and the apiUrl to point to the correct SA-API clone.
 */

// Fallback when VITE_CLONE_LIST_URL isn't configured or the fetch fails -
// keeps existing clone-1..11 behavior working even without the endpoint.
const FALLBACK_CLONES = Array.from({ length: 11 }, (_, i) => i + 1);

// Build the SA-API URL for a given clone environment
function getSaApiUrl(cloneEnvironment, availableClones) {
  const defaultUrl = import.meta.env.VITE_API;

  if (!cloneEnvironment || !availableClones.includes(cloneEnvironment)) {
    return defaultUrl;
  }

  // Clone SA-API URL pattern: https://clone-{N}-sa-api.publishingdashboard.com/api/v1
  return `https://clone-${cloneEnvironment}-sa-api.publishingdashboard.com/api/v1`;
}

function CloneEnvironmentSelector() {
  const cloneEnvironment = useAppStore(state => state.cloneEnvironment);
  const setCloneEnvironment = useAppStore(state => state.setCloneEnvironment);

  // Empty while the clone list fetch is in-flight so the Select shows its
  // built-in loading state; falls back to the static 1-11 list on fetch
  // failure or when VITE_CLONE_LIST_URL isn't configured.
  const cloneListUrl = import.meta.env.VITE_CLONE_LIST_URL;
  const [availableClones, setAvailableClones] = useState(() => (cloneListUrl ? [] : FALLBACK_CLONES));

  // Only show in dev or local environments
  const stage = import.meta.env.VITE_STAGE;
  const showSelector = stage === "dev" || stage === "local";

  useEffect(() => {
    if (!showSelector || !cloneListUrl) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await fetch(cloneListUrl);
        if (!response.ok) {
          throw new Error(`Clone list request failed: ${response.status}`);
        }
        const data = await response.json();
        const clones = Array.isArray(data?.clones) ? data.clones : [];
        if (cancelled)
          return;
        setAvailableClones(clones.length > 0 ? clones : FALLBACK_CLONES);
      }
      catch (error) {
        console.error("Failed to fetch clone list, keeping static fallback:", error);
        if (!cancelled)
          setAvailableClones(FALLBACK_CLONES);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showSelector, cloneListUrl]);

  if (!showSelector) {
    return null;
  }

  const handleChange = (value) => {
    if (value === "default") {
      setCloneEnvironment("default");
      // Reset to default API URL from environment variables
      useAppStore.setState({ apiUrl: import.meta.env.VITE_API });
    }
    else {
      const cloneNum = Number.parseInt(value, 10);
      setCloneEnvironment(cloneNum);
      // Update API URL to point to clone SA-API
      useAppStore.setState({ apiUrl: getSaApiUrl(cloneNum, availableClones) });
    }
  };

  // Build options for the Select component
  const options = [
    ...(stage === "local" ? [{ name: "Default (env var)", value: "default" }] : []),
    ...availableClones.map(n => ({
      name: `Clone ${n}`,
      value: String(n),
    })),
  ];

  const currentValue = cloneEnvironment === "default"
    ? "default"
    : cloneEnvironment
      ? String(cloneEnvironment)
      : (stage === "local" ? "default" : "");

  return (
    <Select
      id="cloneEnvSelect"
      name="cloneEnvSelect"
      className="custom-select text-ink-primary"
      options={options}
      value={currentValue}
      onChange={handleChange}
      placeholder="Environment"
      list
      authSearch
      required
    />
  );
}

export default CloneEnvironmentSelector;
